const coreFeatures = [
  {
    icon: 'ri-user-star-line',
    title: 'Named Provider Assignment',
    desc: "You're not 'next available.' You're assigned a provider who knows your history and health journey.",
  },
  {
    icon: 'ri-message-3-line',
    title: 'Direct Clinical Access',
    desc: 'Secure text access to your care team — real humans, real responses when you need guidance.',
  },
  {
    icon: 'ri-video-chat-line',
    title: 'Unlimited Telehealth',
    desc: 'Care when appropriate, without arbitrary caps. Virtual visits for acute and follow-up needs.',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Monthly In-Clinic Visits',
    desc: 'Unhurried visits focused on outcomes — not volume. Quality time with your provider.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Signature Lab Intelligence™',
    desc: 'Two comprehensive lab panels annually, interpreted in context — not just posted to a portal.',
  },
  {
    icon: 'ri-time-line',
    title: 'After-Hours Guidance',
    desc: 'Because health concerns don\'t follow office hours. Access when you need it most.',
  },
  {
    icon: 'ri-capsule-line',
    title: 'Priority Prescriptions',
    desc: 'Fast RX handling and specialist coordination when needed. Seamless healthcare navigation.',
  },
];

const wellness = [
  { icon: 'ri-heart-pulse-line', title: 'Metabolic & Weight Optimization', desc: 'Evidence-based programs for sustainable health transformation.' },
  { icon: 'ri-leaf-line', title: 'Restorative & Longevity Medicine', desc: 'Advanced therapies focused on optimal aging and vitality.' },
  { icon: 'ri-syringe-line', title: 'IV Therapy & Injectable Nutrients', desc: 'Targeted nutrient delivery for enhanced wellness and recovery.' },
  { icon: 'ri-mental-health-line', title: 'Mental Health Services', desc: 'Comprehensive psychiatric care and therapeutic support.' },
];

const whyWorks = [
  { title: 'Continuity of care', desc: 'Same provider, consistent approach' },
  { title: 'Clear accountability', desc: 'Your provider owns your outcomes' },
  { title: 'Predictable costs', desc: 'No surprise bills or hidden fees' },
  { title: 'Fewer urgent issues', desc: 'Prevention beats crisis management' },
  { title: 'Better outcomes', desc: 'Long-term relationships drive results' },
  { title: 'Designed for modern life', desc: 'Professionals & families welcome' },
];

export default function BenefitsAndFeatures() {
  return (
    <>
      {/* Core Benefits */}
      <section id="benefits" className="py-24 lg:py-32 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-end mb-16">
            <div>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
                What's Included
              </span>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] leading-tight">
                The Signature<br />
                <em className="not-italic text-teal-600">Difference</em>
              </h2>
            </div>
            <p className="text-gray-500 text-lg leading-relaxed lg:pb-2">
              Care when appropriate, without arbitrary caps or insurance-driven decisions. Your health, your way.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {coreFeatures.map((f) => (
              <div
                key={f.title}
                className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-teal-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-teal-50 group-hover:bg-teal-600 rounded-xl mb-5 transition-colors duration-300">
                  <i className={`${f.icon} text-lg text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-sm font-bold text-[#0F1C2E] mb-2 leading-tight">
                  {f.title}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wellness & Optimization */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
              Exclusive Member Pricing
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] mb-5">
              Signature Prevention &amp; Optimization
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Members receive exclusive Signature pricing on advanced wellness services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {wellness.map((w) => (
              <div key={w.title} className="flex items-start gap-5 bg-[#F8FAFB] rounded-2xl p-7 border border-gray-100 hover:border-teal-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                  <i className={`${w.icon} text-xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#0F1C2E] mb-1.5 text-base">{w.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Discount banner */}
          <div className="bg-[#0A2818] rounded-2xl p-8 lg:p-12 text-white">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1.5 bg-teal-500/20 text-teal-400 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                Signature Bonus
              </span>
              <h3 className="font-serif-display text-3xl font-bold">Exclusive Member Pricing</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { pct: '50%', label: 'off Mental Health Visits' },
                { pct: '20%', label: 'off Wellness & Restorative Services' },
                { pct: '10%', label: 'off Additional Primary Care Services' },
              ].map((d) => (
                <div key={d.label} className="text-center bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="font-serif-display text-4xl lg:text-5xl font-bold text-teal-400 mb-2">{d.pct}</div>
                  <div className="text-sm text-white/60">{d.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why it works */}
      <section className="py-24 lg:py-32 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
                The Results
              </span>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] leading-tight mb-8">
                Why Signature™<br />
                <em className="not-italic text-teal-600">Works</em>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyWorks.map((w) => (
                  <div key={w.title} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-teal-600 font-bold"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#0F1C2E]">{w.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{w.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                <img
                  src="https://public.readdy.ai/ai/img_res/adc23bca1323ece9dc5a61da418a480f.jpg distinguished male doctor physician in crisp white medical coat with stethoscope standing inside luxury private medical suite in Las Vegas with dramatic floor to ceiling windows revealing bright Nevada desert valley and Las Vegas city skyline panoramic view bright modern upscale clinical interior warm daylight premium concierge medicine&width=600&height=750&seq=why-works-vegas-mc&orientation=portrait"
                  alt="Med-Care Provider"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 border border-gray-100 max-w-[200px]">
                <div className="flex items-center gap-3 mb-2">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-yellow-400 text-xs"></i>
                  ))}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  "Finally a doctor who actually knows my name."
                </p>
                <p className="text-xs font-semibold text-[#0F1C2E] mt-2">— Signature Member</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
